import { hasCapability } from 'cadenza/data-source-util';
import type { Context } from 'cadenza/query-language/context';
import { createAttributesContext } from 'cadenza/query-language/context';
import { type RootStateOr, getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { isDeactivatedForAnyWorksheet, isHiddenForAnyWorksheet } from 'cadenza/workbook/conditions/condition-util';
import { parseAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionDto, WorkbookConditionValuesDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import {
  areConditionAttributeIdsAccepted,
  areConditionTableColumnsAccepted,
  formatQlExpressionAsString,
  getQlConditionVariables,
  isAttributeRelationalNameAttribute,
  isConditionTypeConvertibleToQl,
} from 'cadenza/workbook/ql-condition/ql-condition-utils';
import {
  getConditionById,
  getConditionDeactivated,
  getDataSourceByObjecttypeId,
  getGlobalConditionIds,
  getObjecttypeById,
  getVisibleObjecttypes,
  resolveObjecttypeAttributes,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export function formatQlConditionValuesAsText(state: RootStateOr<WorkbookState>, values: WorkbookConditionValuesDto) {
  const condition = {
    conditionType: values.conditionType,
    values,
  };
  const visibleObjecttypes = resolveObjecttypeAttributes(getVisibleObjecttypes(state));
  const attributes = visibleObjecttypes.flatMap((ot) => ot.attributes);
  const attributesContext = createAttributesContext(attributes);
  const context: Context = {
    usableAttributes: attributesContext,
    allAttributes: attributesContext,
    variables: getQlConditionVariables(condition),
    mode: 'Condition',
  };
  return formatQlExpressionAsString(context, condition);
}

/**
 * Checks if condition can be converted to QL condition.
 *
 * @param condition - Condition to check
 * @param acceptedAttributes - attributes accepted for QL condition
 * @return whether condition can be converted to QL condition
 */
export function isConditionConvertibleToQl(condition: ConditionDto, acceptedAttributes: WorkbookAttributeDto[]) {
  return (
    condition.status === 'VALID' &&
    isConditionTypeConvertibleToQl(condition.type) &&
    areConditionTableColumnsAccepted(condition.tableColumnsDefinitionDto) &&
    areConditionAttributeIdsAccepted(acceptedAttributes, ...condition.attributeIds) &&
    areConditionAttributeObjecttypesValid(condition.attributeIds) &&
    areNoConditionsDeactivated([condition.id])
  );
}

/**
 * Checks if the given condition contains only attributes that belong to OTs that come from
 * datasources that support the QL_CONDITIONS capability and if idAttributes are accepted.
 *
 * @param attributeIds - Condition to check
 * @return true if the condition's datasources support QL conditions
 */
function areConditionAttributeObjecttypesValid(attributeIds: AttributeId[]) {
  return attributeIds.every((attributeId) => {
    const [objecttypeId] = parseAttributeId(attributeId);
    const objecttype = getObjecttypeById(getStore().getState(), objecttypeId);
    return (
      objecttype &&
      hasCapability(getDataSourceByObjecttypeId(getStore().getState(), objecttype.id), 'QL_CONDITIONS') &&
      !isAttributeRelationalNameAttribute(objecttype, attributeId)
    );
  });
}

// duplicated from `condition-workbook-utils` to avoid a circular dependency
function areNoConditionsDeactivated(conditionIds: ConditionId[]) {
  // If all the conditions are deactivated, there should be only action to activate
  return conditionIds.every((conditionId) => getConditionDeactivated(getStore().getState(), conditionId) === false);
}

export function isConversionToQlConditionAllowed() {
  // QL conversion should be disabled if any condition is deactivated
  // (also any other one).
  // this must actually ask for all conditions, not only for this one.
  const globalConditionIds = getGlobalConditionIds(getStore().getState());
  return (
    areNoConditionsDeactivated(globalConditionIds) &&
    globalConditionIds.every(
      (conditionId) =>
        !isHiddenForAnyWorksheet(getConditionById(getStore().getState(), conditionId)) &&
        !isDeactivatedForAnyWorksheet(getConditionById(getStore().getState(), conditionId)),
    )
  );
}
